import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { firebaseAuth } from '@/lib/firebase';
import { userService } from '@/services/userService';
import { driverService } from '@/services/driverService';
import type { Passenger, Driver, Admin, UserType } from '@/types';

interface AuthContextType {
  // User state
  currentUser: Passenger | Driver | Admin | null;
  userType: UserType | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Auth methods
  loginAsPassenger: (phone: string, password: string) => Promise<void>;
  loginAsDriver: (phone: string, password: string) => Promise<void>;
  loginAsOwner: (phone: string, password: string) => Promise<boolean>;
  registerPassenger: (data: RegisterPassengerData) => Promise<void>;
  registerDriver: (data: RegisterDriverData) => Promise<void>;
  logout: () => Promise<void>;
  
  // Profile methods
  updateProfile: (data: Partial<Passenger | Driver>) => Promise<void>;
  uploadProfilePhoto: (file: File) => Promise<string>;
  
  // Owner credentials
  ownerCredentials: { phone: string; password: string };
  updateOwnerCredentials: (phone: string, password: string) => void;
}

interface RegisterPassengerData {
  name: string;
  phone: string;
  email?: string;
  password: string;
}

interface RegisterDriverData {
  name: string;
  phone: string;
  email?: string;
  password: string;
  vehicle: {
    type: 'economy' | 'comfort' | 'premium' | 'family' | 'delivery';
    make: string;
    model: string;
    year: number;
    color: string;
    plateNumber: string;
    plateCity: string;
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Owner credentials - stored securely in production
const DEFAULT_OWNER_CREDENTIALS = {
  phone: '0912345678',
  password: 'RahlaOwner2024!',
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<Passenger | Driver | Admin | null>(null);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [ownerCredentials, setOwnerCredentials] = useState(DEFAULT_OWNER_CREDENTIALS);

  // Check for saved session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const savedUser = localStorage.getItem('rahla_user');
      const savedType = localStorage.getItem('rahla_user_type');
      
      if (savedUser && savedType) {
        try {
          const userData = JSON.parse(savedUser);
          setCurrentUser(userData);
          setUserType(savedType as UserType);
        } catch (error) {
          console.error('Error parsing saved user:', error);
          localStorage.removeItem('rahla_user');
          localStorage.removeItem('rahla_user_type');
        }
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  // Save session when user changes
  useEffect(() => {
    if (currentUser && userType) {
      localStorage.setItem('rahla_user', JSON.stringify(currentUser));
      localStorage.setItem('rahla_user_type', userType);
    } else {
      localStorage.removeItem('rahla_user');
      localStorage.removeItem('rahla_user_type');
    }
  }, [currentUser, userType]);

  const loginAsPassenger = useCallback(async (phone: string, password: string) => {
    setIsLoading(true);
    try {
      // In production, authenticate with Firebase Auth
      // For demo, we'll simulate authentication
      const email = `${phone}@rahla.com`;
      
      // Try to get existing passenger
      // In production: const userCredential = await firebaseAuth.loginWithEmail(email, password);
      
      // For demo, create or get passenger
      const passengerId = `passenger_${phone}`;
      let passenger = await userService.getPassenger(passengerId);
      
      if (!passenger) {
        // Create new passenger for demo
        await userService.createPassenger(passengerId, {
          name: 'راكب',
          phone,
          email,
        });
        passenger = await userService.getPassenger(passengerId);
      }
      
      if (passenger) {
        setCurrentUser(passenger);
        setUserType('passenger');
      } else {
        throw new Error('Failed to authenticate');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginAsDriver = useCallback(async (phone: string, password: string) => {
    setIsLoading(true);
    try {
      const email = `${phone}@rahla.com`;
      const driverId = `driver_${phone}`;
      
      let driver = await driverService.getDriver(driverId);
      
      if (!driver) {
        throw new Error('Driver not found');
      }
      
      setCurrentUser(driver);
      setUserType('driver');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginAsOwner = useCallback(async (phone: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      if (phone === ownerCredentials.phone && password === ownerCredentials.password) {
        const owner: Admin = {
          id: 'owner_001',
          name: 'مالك التطبيق',
          phone: ownerCredentials.phone,
          type: 'owner',
          rating: 0,
          totalRides: 0,
          createdAt: new Date(),
          updatedAt: new Date(),
          isActive: true,
          permissions: ['all'],
        };
        
        setCurrentUser(owner);
        setUserType('owner');
        return true;
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [ownerCredentials]);

  const registerPassenger = useCallback(async (data: RegisterPassengerData) => {
    setIsLoading(true);
    try {
      const email = `${data.phone}@rahla.com`;
      const passengerId = `passenger_${data.phone}`;
      
      // In production: await firebaseAuth.registerWithEmail(email, data.password);
      
      await userService.createPassenger(passengerId, {
        name: data.name,
        phone: data.phone,
        email: data.email,
      });
      
      const passenger = await userService.getPassenger(passengerId);
      if (passenger) {
        setCurrentUser(passenger);
        setUserType('passenger');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const registerDriver = useCallback(async (data: RegisterDriverData) => {
    setIsLoading(true);
    try {
      const email = `${data.phone}@rahla.com`;
      const driverId = `driver_${data.phone}`;
      
      // In production: await firebaseAuth.registerWithEmail(email, data.password);
      
      await driverService.createDriver(driverId, {
        name: data.name,
        phone: data.phone,
        email: data.email,
        vehicle: data.vehicle,
      });
      
      const driver = await driverService.getDriver(driverId);
      if (driver) {
        setCurrentUser(driver);
        setUserType('driver');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      // In production: await firebaseAuth.logout();
      setCurrentUser(null);
      setUserType(null);
      localStorage.removeItem('rahla_user');
      localStorage.removeItem('rahla_user_type');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (data: Partial<Passenger | Driver>) => {
    if (!currentUser || !userType) return;
    
    if (userType === 'passenger') {
      await userService.updatePassenger(currentUser.id, data as Partial<Passenger>);
      const updated = await userService.getPassenger(currentUser.id);
      if (updated) setCurrentUser(updated);
    } else if (userType === 'driver') {
      await driverService.updateDriver(currentUser.id, data as Partial<Driver>);
      const updated = await driverService.getDriver(currentUser.id);
      if (updated) setCurrentUser(updated);
    }
  }, [currentUser, userType]);

  const uploadProfilePhoto = useCallback(async (file: File): Promise<string> => {
    if (!currentUser || !userType) throw new Error('Not authenticated');
    
    if (userType === 'passenger') {
      return await userService.uploadProfilePhoto(currentUser.id, file);
    } else if (userType === 'driver') {
      return await driverService.uploadDriverPhoto(currentUser.id, file);
    }
    
    throw new Error('Invalid user type');
  }, [currentUser, userType]);

  const updateOwnerCredentials = useCallback((phone: string, password: string) => {
    setOwnerCredentials({ phone, password });
    // In production, save to secure storage
    localStorage.setItem('rahla_owner_phone', phone);
    localStorage.setItem('rahla_owner_password', password);
  }, []);

  // Load saved owner credentials on mount
  useEffect(() => {
    const savedPhone = localStorage.getItem('rahla_owner_phone');
    const savedPassword = localStorage.getItem('rahla_owner_password');
    if (savedPhone && savedPassword) {
      setOwnerCredentials({ phone: savedPhone, password: savedPassword });
    }
  }, []);

  const value: AuthContextType = {
    currentUser,
    userType,
    isAuthenticated: !!currentUser,
    isLoading,
    loginAsPassenger,
    loginAsDriver,
    loginAsOwner,
    registerPassenger,
    registerDriver,
    logout,
    updateProfile,
    uploadProfilePhoto,
    ownerCredentials,
    updateOwnerCredentials,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
